import { Outlet } from 'react-router-dom';
import {
  AppShell,
  Avatar,
  IconBilling,
  IconChecklistTemplates,
  IconClients,
  IconCommissions,
  IconOrders,
  IconRates,
  IconRevenue,
  IconSubscriptions,
  IconUsers,
  useCurrentUserOptional,
  type SidebarSection,
} from '@jywer/ui';

/**
 * Wave 7 (BUILD_PLAN decision #18 / PR #20) — admin sidebar v3 polish.
 *
 * Splits the previous one-section layout into three semantic groups
 * (Operationer / Data / Indstillinger), giving each item a v3-token
 * icon, and dropping in a footer with the current user (mirrors the
 * main app v3 sidebar). The Sidebar primitive itself is unchanged —
 * this PR is purely a tokens + grouping migration.
 */
const sections: SidebarSection[] = [
  {
    label: 'Operationer',
    items: [
      { label: 'Billing', to: '/billing', viewId: 'view:admin.billing', icon: <IconBilling /> },
      { label: 'Ordrer', to: '/orders', viewId: 'view:admin.orders', icon: <IconOrders /> },
      {
        label: 'Abonnementer',
        to: '/subscriptions',
        viewId: 'view:admin.subscriptions',
        icon: <IconSubscriptions />,
      },
    ],
  },
  {
    label: 'Data',
    items: [
      { label: 'Clients', to: '/clients', viewId: 'view:admin.clients', icon: <IconClients /> },
      { label: 'Rates', to: '/rates', viewId: 'view:admin.rates', icon: <IconRates /> },
      {
        label: 'Commissions',
        to: '/commissions',
        viewId: 'view:admin.commissions',
        icon: <IconCommissions />,
      },
      {
        label: 'Revenue Pipeline',
        to: '/revenue-pipeline',
        viewId: 'view:admin.revenue-pipeline',
        icon: <IconRevenue />,
      },
    ],
  },
  {
    label: 'Indstillinger',
    items: [
      { label: 'Users', to: '/users', viewId: 'view:admin.users', icon: <IconUsers /> },
      {
        label: 'Checklist-skabeloner',
        to: '/checklist-templates',
        viewId: 'edit:admin.checklist-templates',
        icon: <IconChecklistTemplates />,
      },
    ],
  },
];

function CurrentUserFooter() {
  const user = useCurrentUserOptional();
  return (
    <div className="flex items-center gap-2">
      <Avatar name={user?.displayName ?? user?.email ?? '—'} size={26} outline="var(--paper-1)" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-ui font-medium text-ink-0">
          {user?.displayName ?? user?.email ?? '—'}
        </div>
        <div className="truncate font-mono text-micro tracking-[0.04em] text-ink-2">
          {user?.email ?? ''}
        </div>
      </div>
    </div>
  );
}

export function AppLayout() {
  return (
    <AppShell
      title="JYWER"
      subtitle="Admin workspace"
      sections={sections}
      sidebarFooter={<CurrentUserFooter />}
    >
      <Outlet />
    </AppShell>
  );
}
